'use client';

// Global
import React, { useState, useEffect, useRef, useCallback, useTransition } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { type Field, type LinkField } from '@sitecore-content-sdk/nextjs';
import scConfig from 'sitecore.config';
import { tv } from 'tailwind-variants';
import Select, { StylesConfig } from 'react-select';
import { sendGTMEvent } from '@next/third-parties/google';

// Local
import { useCloudSearch } from 'lib/hooks/search/useCloudSearch';
import { getTestProps } from 'lib/testing/utils';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { RemixIcon } from 'helpers/RemixIcon';
import useDictionary from 'lib/hooks/useDictionary';
import SkeletonCard from 'helpers/SkeletonCard/SkeletonCard';
import SkeletonFilterPanel from 'helpers/SkeletonCard/SkeletonFilterPanel';
import {
  getSelectStyles as getFormSelectStyles,
  DropdownIndicator,
} from 'helpers/FormFields/SelectField';
import {
  trackSearchItemClick,
  trackSearchSuggestionClick,
} from 'lib/search/analytics';

type TopExpert = {
  id: string;
  firstname?: { jsonValue: { value: string } };
  lastname?: { jsonValue: { value: string } };
  jobtitle?: { jsonValue: { value: string } };
  quoteText?: { jsonValue: { value: string } };
  picture?: { jsonValue: { value: { src: string; alt?: string } } };
  linkedInURL?: { jsonValue: { value: string } };
  url?: { url: string };
};

type GraphQLLinkField = {
  jsonValue?: {
    value?: {
      href?: string;
      text?: string;
      linktype?: string;
      [key: string]: unknown;
    };
  };
};

type ColCount = '1' | '2' | '3' | '4' | undefined;

type SearchResultsProps = {
  rfkId: string;
  searchPlaceholder?: Field<string>;
  noSearchResult?: Field<string>;
  filterText?: Field<string>;
  cardsCTAText?: Field<string>;
  source?: Field<string>;
  cta1?: LinkField | GraphQLLinkField;
  cta2?: LinkField | GraphQLLinkField;
  topExperts?: TopExpert[];
  pageTemplateId?: string;
  colCount?: ColCount;
};

interface SelectOptionType {
  value: string;
  label: string;
}

export const ExpertListWithSearchComponent = ({
  rfkId,
  searchPlaceholder,
  noSearchResult,
  filterText,
  cardsCTAText,
  cta1,
  cta2,
  topExperts,
  pageTemplateId,
  colCount,
}: SearchResultsProps) => {
  // Expert Profiles Landing Page shows 6 experts, all other pages show 3
  const EXPERT_PROFILES_LANDING_TEMPLATE_ID = 'a658abda-3369-4a2b-88ec-82f5ea89b3b6';
  const isLandingPage =
    pageTemplateId?.toLowerCase() === EXPERT_PROFILES_LANDING_TEMPLATE_ID.toLowerCase();
  const maxVisibleExperts = isLandingPage ? 6 : Number(colCount) || 3;

  // Transform CTA data from GraphQL format to LinkField format
  const cta1Field = React.useMemo(
    () =>
      (cta1 as GraphQLLinkField)?.jsonValue
        ? { value: (cta1 as GraphQLLinkField).jsonValue?.value }
        : (cta1 as LinkField),
    [cta1]
  );

  const cta2Field = React.useMemo(
    () =>
      (cta2 as GraphQLLinkField)?.jsonValue
        ? { value: (cta2 as GraphQLLinkField).jsonValue?.value }
        : (cta2 as LinkField),
    [cta2]
  );

  const router = useRouter();
  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();
  const defaultSiteLocale =
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en';
  const { getDictionaryValue } = useDictionary();

  const cloudSdkSearch = React.useMemo(() => {
    const edge = scConfig.api?.edge as { contextId?: string; clientContextId?: string } | undefined;
    return {
      sitecoreEdgeContextId: edge?.clientContextId || edge?.contextId,
      siteName: scConfig.defaultSite,
      sitecoreEdgeUrl:
        (typeof process !== 'undefined' &&
          (process.env.NEXT_PUBLIC_SITECORE_EDGE_URL || process.env.SITECORE_EDGE_URL)) ||
        undefined,
    };
  }, []);

  // Refs for managing focus and blur behavior
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const isClearingRef = useRef(false);

  // Initialize state from URL params to prevent flickering on remount
  const [searchQuery, setSearchQuery] = useState(() => {
    const search = searchParams.get('search');
    return search && typeof search === 'string' ? search : '';
  });
  const [submittedSearch, setSubmittedSearch] = useState(() => {
    const search = searchParams.get('search');
    return search && typeof search === 'string' ? search : '';
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [exactMatchName, setExactMatchName] = useState<string | null>(() => {
    const suggestionContext = searchParams.get('suggestionContext');
    if (suggestionContext && typeof suggestionContext === 'string') {
      const nameOnly = suggestionContext
        .split(/\s*[|\-]\s*/)[0]
        .trim()
        .toLowerCase();
      return nameOnly;
    }
    return null;
  });
  const [fullSuggestionContext, setFullSuggestionContext] = useState<string | null>(() => {
    const suggestionContext = searchParams.get('suggestionContext');
    const search = searchParams.get('search');
    // Only set suggestion context if we also have a search term
    if (suggestionContext && typeof suggestionContext === 'string' && search) {
      return suggestionContext;
    }
    return null;
  });
  const [selectedFilters, setSelectedFilters] = useState<{
    service?: string;
    location?: string;
    expertise?: string;
  }>(() => {
    const filters: { service?: string; location?: string; expertise?: string } = {};
    const expertise = searchParams.get('expertise');
    const location = searchParams.get('location');
    const service = searchParams.get('service');
    if (expertise && typeof expertise === 'string') filters.expertise = expertise;
    if (location && typeof location === 'string') filters.location = location;
    if (service && typeof service === 'string') filters.service = service;
    return filters;
  });
  const [displayCount, setDisplayCount] = useState(maxVisibleExperts);
  // useTransition keeps the existing list visible during re-renders that
  // would otherwise trip the parent Suspense boundary (something inside this
  // widget's tree suspends briefly on each re-render, causing the list to
  // flash empty without this). Used by every handler that mutates state
  // which triggers a re-render of the results grid.
  const [, startWidgetTransition] = useTransition();

  // Build CTA2 URL with current search and filters
  const cta2WithParams = React.useMemo(() => {
    if (!cta2Field?.value?.href) return cta2Field;

    const params = new URLSearchParams();
    if (submittedSearch) params.append('search', submittedSearch);
    // Only include suggestionContext if there's also a search term
    if (fullSuggestionContext && submittedSearch)
      params.append('suggestionContext', fullSuggestionContext);
    if (selectedFilters.expertise) params.append('expertise', selectedFilters.expertise);
    if (selectedFilters.location) params.append('location', selectedFilters.location);
    if (selectedFilters.service) params.append('service', selectedFilters.service);

    const queryString = params.toString();
    if (!queryString) return cta2Field;

    return {
      value: {
        ...cta2Field.value,
        href: `${cta2Field.value.href}?${queryString}`,
      },
    } as LinkField;
  }, [cta2Field, submittedSearch, fullSuggestionContext, selectedFilters]);

  // Use environment variable for expert profiles source ID, with Sitecore value as fallback
  const expertProfilesSourceId = process.env.NEXT_PUBLIC_SEARCH_EXPERT_PROFILES_SOURCE_ID;

  // Ensure 'source' field is set in Sitecore for all environments
  const searchSourceIds = React.useMemo(
    () => (expertProfilesSourceId ? ([expertProfilesSourceId] as [string, ...string[]]) : undefined),
    [expertProfilesSourceId]
  );

  const searchLocale = React.useMemo(
    () => ({
      language: defaultSiteLocale.split('-')[0] || 'en',
      country: defaultSiteLocale.split('-')[1]?.toLowerCase() || 'us',
    }),
    [defaultSiteLocale]
  );

  const cloudSearchConfig = React.useMemo(
    () => ({
      widgetType: 'expertprofile' as const,
      widgetId: rfkId,
      maxResults: 100,
      sources: searchSourceIds,
      cloudSdkSearch,
      locale: searchLocale,
    }),
    [rfkId, searchSourceIds, cloudSdkSearch, searchLocale]
  );

  const {
    data,
    loading,
    initialSearchSettled,
    error,
    performSearch,
    suggestions,
    loadingSuggestions,
    getSuggestions: fetchSuggestions,
    clearSuggestions,
  } = useCloudSearch(cloudSearchConfig);

  // Trigger initial search based on URL params (state is already initialized from URL in useState)
  useEffect(() => {
    if (!searchSourceIds || searchSourceIds.length === 0) return;
    const search = searchParams.get('search');
    const expertise = searchParams.get('expertise');
    const location = searchParams.get('location');
    const service = searchParams.get('service');

    const searchTerm = search && typeof search === 'string' ? search : '';
    const filters: { service?: string; location?: string; expertise?: string } = {};

    if (expertise && typeof expertise === 'string') filters.expertise = expertise;
    if (location && typeof location === 'string') filters.location = location;
    if (service && typeof service === 'string') filters.service = service;

    // Build filter config from URL params if filters exist
    const filterConfig = Object.keys(filters).length > 0 ? { facetFilters: filters } : undefined;

    void performSearch(searchTerm, filterConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const experts = data?.content || [];
  const currentFacets = data?.facet || [];

  // First request: skeleton until hook `finally` runs. Later: skeleton only while loading with no rows yet (keep showing cards if we still have previous results).
  const showExpertResultsSkeleton =
    !initialSearchSettled || (loading && experts.length === 0 && !error);

  // Use facets directly from API - they reflect current filters
  const facets = currentFacets;

  // Check if there are active filters
  const hasActiveFilters = Object.values(selectedFilters).some((v) => v);

  // Track when initial data has loaded to prevent showing top experts prematurely

  // Track the last search term that triggered a GTM event to prevent duplicate triggers
  // Use sessionStorage to persist across component unmounts/mounts
  // Use page URL to make it unique per page/component instance
  const getStorageKey = useCallback(() => {
    const currentPageUrl =
      typeof window !== 'undefined' ? window.location.pathname : pathname || '';
    return `expertList_lastGtmSearchTerm_${currentPageUrl}`;
  }, [pathname]);

  // useRef instead of useState: GTM deduplication never affects rendered output,
  // so storing it as state was adding a needless re-render per search.
  const lastGtmSearchTermRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      lastGtmSearchTermRef.current = sessionStorage.getItem(getStorageKey()) || null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSearchInputStorageKey = useCallback(
    () => `${getStorageKey()}_searchInput`,
    [getStorageKey]
  );
  const getSuggestionPositionStorageKey = useCallback(
    () => `${getStorageKey()}_suggestionPosition`,
    [getStorageKey]
  );

  const storeSearchClickContext = useCallback(
    (inputValue: string, suggestionPosition?: number) => {
      if (typeof window === 'undefined') {
        return;
      }
      sessionStorage.setItem(getSearchInputStorageKey(), inputValue);
      if (typeof suggestionPosition === 'number' && suggestionPosition > 0) {
        sessionStorage.setItem(getSuggestionPositionStorageKey(), String(suggestionPosition));
      } else {
        sessionStorage.removeItem(getSuggestionPositionStorageKey());
      }
    },
    [getSearchInputStorageKey, getSuggestionPositionStorageKey]
  );

  // Primitives only: `data` is a new object every fetch; listing it caused the GTM effect to re-run
  // on every search response and amplified any feedback loops with third-party / analytics code.
  const searchResultCountForGtm = loading ? undefined : data != null ? (data.content?.length ?? 0) : undefined;

  useEffect(() => {
    if (searchResultCountForGtm === undefined) {
      return;
    }
    if (submittedSearch && submittedSearch !== lastGtmSearchTermRef.current) {
      // Read from sessionStorage so values survive remount after router.push
      let searchInputForEvent = submittedSearch;
      let suggestionPositionForEvent: number | string = '';
      if (typeof window !== 'undefined') {
        const storedSearchInput = sessionStorage.getItem(getSearchInputStorageKey());
        if (storedSearchInput !== null) {
          searchInputForEvent = storedSearchInput;
          sessionStorage.removeItem(getSearchInputStorageKey());
        }
        const storedSuggestionPosition = sessionStorage.getItem(getSuggestionPositionStorageKey());
        if (storedSuggestionPosition !== null) {
          const parsedPosition = Number(storedSuggestionPosition);
          suggestionPositionForEvent = Number.isNaN(parsedPosition)
            ? storedSuggestionPosition
            : parsedPosition;
          sessionStorage.removeItem(getSuggestionPositionStorageKey());
        }
      }

      sendGTMEvent({
        event: 'search_click',
        search_term: submittedSearch,
        search_type: 'people',
        search_input: searchInputForEvent,
        suggestion_position: suggestionPositionForEvent,
        results: searchResultCountForGtm > 0 ? 'true' : 'false',
      });
      lastGtmSearchTermRef.current = submittedSearch;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(getStorageKey(), submittedSearch);
      }
    }
  }, [
    loading,
    searchResultCountForGtm,
    submittedSearch,
    getStorageKey,
    getSearchInputStorageKey,
    getSuggestionPositionStorageKey,
  ]);

  // Ensure rfkId has a value (check after all hooks)
  if (!rfkId) {
    console.error('ExpertList: rfkId is required');
    return (
      <div>{getDictionaryValue('ExpertListWidgetIdError') || 'Error: Widget ID is required'}</div>
    );
  }

  // Check if we should show top experts (only on initial load with no submitted search/filters)
  const hasActiveSearch = submittedSearch || exactMatchName;
  const shouldShowTopExperts =
    !hasActiveSearch && !hasActiveFilters && topExperts && topExperts.length > 0;

  // Convert top experts to the same format as Cloud SDK experts
  const formattedTopExperts = shouldShowTopExperts
    ? topExperts.map((expert) => {
        const firstName = expert.firstname?.jsonValue?.value || '';
        const lastName = expert.lastname?.jsonValue?.value || '';
        return {
          id: expert.id,
          name: `${firstName} ${lastName}`.trim(),
          first_name: firstName,
          last_name: lastName,
          job_title: expert.jobtitle?.jsonValue?.value || '',
          picture: expert.picture?.jsonValue?.value?.src || '',
          quote: expert.quoteText?.jsonValue?.value || '',
          linked_in_url: expert.linkedInURL?.jsonValue?.value || '',
          url: expert.url?.url || '',
        };
      })
    : [];

  // Filter for exact match if a suggestion was clicked (fallback to full list if nothing matches)
  const exactMatchFilteredExperts = exactMatchName
    ? (() => {
        const filtered = experts.filter((expert) => {
          const fullName = `${expert.first_name || ''} ${expert.last_name || ''}`
            .trim()
            .toLowerCase();
          const name = (expert.name || '')
            .split(/\s*[|\-]\s*/)[0]
            .trim()
            .toLowerCase();
          return fullName === exactMatchName || name === exactMatchName;
        });
        return filtered.length > 0 ? filtered : experts;
      })()
    : experts;

  // Cloud SDK handles filtering server-side, so no client-side filtering needed
  // Only apply exact match filter if a suggestion was clicked
  const filteredExperts = exactMatchFilteredExperts;

  // Combine top experts with search results (top experts first, then Cloud SDK results)
  // Deduplicate by URL to prevent the same expert from appearing twice
  const allExperts = shouldShowTopExperts
    ? (() => {
        const remainingExperts = filteredExperts
          .map((expert) => {
            const expertFirstName = expert.first_name?.trim() || '';
            const expertLastName = expert.last_name?.trim() || '';
            const expertJobTitle = expert.job_title?.trim() || '';
            const remainingTopExpertList = formattedTopExperts.filter((topExpert) => {
              return (
                (topExpert.first_name || '').trim() === expertFirstName &&
                (topExpert.last_name || '').trim() === expertLastName &&
                (topExpert.job_title || '').trim() === expertJobTitle
              );
            });
            // Exclude experts that match topExperts (return null), include others (return expert)
            return remainingTopExpertList.length > 0 ? null : expert;
          })
          .filter((expert): expert is NonNullable<typeof expert> => expert !== null);

        return [...formattedTopExperts, ...remainingExperts];
      })()
    : filteredExperts;

  const visibleExperts = allExperts.slice(0, displayCount);
  const hasMoreExperts = allExperts.length > displayCount;

  const handleShowMore = (e?: React.MouseEvent<HTMLButtonElement>) => {
    // Drop focus before layout shift so the browser doesn't auto-scroll to keep the (now-moved) focused element in view.
    e?.currentTarget?.blur();
    startWidgetTransition(() => {
    setDisplayCount((prev) => prev + 3);
    });
  };

  const removeSpecialCharacters = (value: string) => {
    return value.replace(/[^a-zA-Z0-9- ]/g, '');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = removeSpecialCharacters(e.target.value);
    setSearchQuery(value);
    setSelectedSuggestionIndex(-1);

    // If input is cleared, reset to show all results and clear submitted search
    if (value.trim().length === 0) {
      setShowSuggestions(false);
      clearSuggestions(); // Clear suggestions via the hook
      setFullSuggestionContext(null); // Always clear suggestion context when input is cleared

      // Only perform search if there was an active search/exact match that needs clearing
      const hadActiveSearch = submittedSearch || exactMatchName;
      if (hadActiveSearch) {
        // Group the reset state updates + new search inside a transition so the
        // existing results stay visible until the new query resolves.
        startWidgetTransition(() => {
        setSubmittedSearch('');
        setExactMatchName(null);
        lastGtmSearchTermRef.current = null;
        if (typeof window !== 'undefined') sessionStorage.removeItem(getStorageKey());
        setDisplayCount(maxVisibleExperts); // Reset display count
        performSearch(''); // Search with empty query to get all results
        });
      }
      return;
    }

    // Show suggestions and fetch them as user types
    setShowSuggestions(true);
    if (value.length >= 2) {
      fetchSuggestions(value);
    }
  };

  const handleSearchSubmit = (query?: string, suggestionCtx?: string) => {
    const searchTerm = query || searchQuery;

    if (searchTerm && searchTerm.trim().length === 0) {
      setSearchQuery('');
      return;
    }

    // Regular search (not from suggestion): capture typed input, clear suggestion position
    if (!suggestionCtx) {
      storeSearchClickContext(searchTerm.trim());
    }

    // Hide suggestions outside the transition so the dropdown closes
    // immediately (this update is urgent / user-perceived).
    setShowSuggestions(false);

    // Group all state updates + the search call inside a transition so the
    // current list stays visible while the new query resolves.
    startWidgetTransition(() => {
    setSubmittedSearch(searchTerm);

    // Only clear exact match if this is NOT from a suggestion click
    if (!suggestionCtx) {
      setExactMatchName(null);
      setFullSuggestionContext(null); // Clear suggestion context for regular searches
    }
    // Reset display count when searching
    setDisplayCount(maxVisibleExperts);
    performSearch(searchTerm);

    // Update URL with search parameter on landing page
    if (isLandingPage) {
      const urlParams = new URLSearchParams();
      if (searchTerm) urlParams.append('search', searchTerm);
      // Add suggestion context if this is from a suggestion click
      if (suggestionCtx) {
        urlParams.append('suggestionContext', suggestionCtx);
      }
      // reset filters
      setSelectedFilters({ expertise: '', location: '', service: '' });
      const currentPath = pathname;
        const newUrl = urlParams.toString()
          ? `${currentPath}?${urlParams.toString()}`
          : currentPath;

        // { scroll: false }: Next.js App Router scrolls to top by default on
        // router.push. We want the user to stay where they are when filters /
        // searches update the URL query string.
        router.push(newUrl, { scroll: false });
    }
    });
  };

  const handleSuggestionClick = (
    suggestion: string,
    suggestionIndex: number,
    e?: React.MouseEvent
  ) => {
    // Prevent default to avoid any potential issues
    e?.preventDefault();
    e?.stopPropagation();

    // Extract just the name part (before the first " | " or " - ")
    const nameOnly = suggestion
      .split(/\s*[|\-]\s*/)[0]
      .trim()
      .toLowerCase();

    // Persist before router.push — remount would wipe an in-memory ref
    storeSearchClickContext(
      searchQuery.trim(),
      suggestionIndex >= 0 ? suggestionIndex + 1 : undefined
    );

    void trackSearchSuggestionClick({
      widgetId: rfkId || 'rfkid_expert_profile',
      filters: [
        {
          name: 'expert_name_suggester',
          title: 'expert_name_suggester',
          value: nameOnly,
          valuePosition: suggestionIndex >= 0 ? suggestionIndex : 0,
          displayName: suggestion,
        },
      ],
      request: {
        keyword: searchQuery.trim() || undefined,
      },
    });

    // Urgent UI updates: close dropdown and reflect chosen value in input
    // immediately. These are kept outside the transition so the user sees
    // their click acknowledged instantly.
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    setSearchQuery(nameOnly);

    // Group the result-affecting updates inside a transition so the list
    // does not flash empty while the new search resolves.
    startWidgetTransition(() => {
    setExactMatchName(nameOnly); // Set exact match filter
    setFullSuggestionContext(suggestion); // Store full suggestion for URL params

    // Clear dropdown filters when searching for a specific person
    setSelectedFilters({});

    // Search for the exact name
    // The API may return multiple partial matches, but we'll filter to exact match in the UI
    // Pass the suggestion context so it's preserved in the URL
    handleSearchSubmit(nameOnly, suggestion);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearchSubmit();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSuggestionClick(
            suggestions[selectedSuggestionIndex].text,
            selectedSuggestionIndex
          );
        } else {
          handleSearchSubmit();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const handleFilterChange = (facetName: string, selectedOption: SelectOptionType | null) => {
    const value = selectedOption?.value || '';
    const previousRaw = selectedFilters[facetName as keyof typeof selectedFilters];
    const previousValue = previousRaw == null || previousRaw === '' ? '' : previousRaw;
    const nextValue = value || '';
    // react-select can fire onChange when options/value references change after a search; skip no-ops
    // to avoid performSearch → setState → re-render → onChange loops.
    if (nextValue === previousValue) {
      return;
    }

    // Build new filters - remove the key entirely if value is empty
    const newFilters = { ...selectedFilters };
    if (value) {
      newFilters[facetName as keyof typeof selectedFilters] = value;

      // send GTM event for filter click  when the value is not set to ALL
      sendGTMEvent({
        event: 'filter_click',
        service: newFilters['service'] || 'NA',
        location: newFilters['location'] || 'NA',
        expertise: newFilters['expertise'] || 'NA',
        search_type: 'people',
        content_group: 'our-people',
      });
    } else {
      delete newFilters[facetName as keyof typeof selectedFilters];
    }

    // Group filter state + URL update + search call inside a transition so
    // the existing cards remain visible until the filtered results are ready.
    startWidgetTransition(() => {
    setSelectedFilters(newFilters);

    // Update browser URL when filters change on landing page
    if (isLandingPage) {
      const urlParams = new URLSearchParams();
      if (submittedSearch) urlParams.append('search', submittedSearch);
      if (newFilters.expertise) urlParams.append('expertise', newFilters.expertise);
      if (newFilters.location) urlParams.append('location', newFilters.location);
      if (newFilters.service) urlParams.append('service', newFilters.service);

      const currentPath = pathname;
        const newUrl = urlParams.toString()
          ? `${currentPath}?${urlParams.toString()}`
          : currentPath;

        // { scroll: false }: Next.js App Router scrolls to top by default on
        // router.push. We want the user to stay where they are when filters /
        // searches update the URL query string.
        router.push(newUrl, { scroll: false });
    }

    // Reset display count when filtering
    setDisplayCount(maxVisibleExperts);

    // Use ALL filters to filter results via facet.types
    // facet.types both filters results AND controls facet behavior
    const filterConfig = {
      facetFilters: newFilters,
    };

    // Perform search with facet filters
    performSearch(submittedSearch, filterConfig);
    });
  };

  const {
    container,
    searchContainer,
    searchSection,
    searchWrapper,
    searchInput,
    searchIcon,
    clearButton,
    loadingSpinnerContainer,
    searchButton,
    relativeWrapper,
    suggestionsDropdown,
    suggestionsLabel,
    suggestionsList,
    suggestionItem,
    suggestionContent,
    suggestionText,
    suggestionName,
    suggestionRest,
    suggestionIcon,
    loadingSpinnerIcon,
    filtersNotAvailable,
    filtersSection,
    filterLabel,
    filterDropdowns,
    resultsSection,
    expertGrid,
    ctaSection,
    ctaButton,
    noResults,
    noResultsText,
    noResultsCtaSection,
    noResultsCtaButton,
    expertCard,
    imageSection,
    contentSection,
    contentTop,
    contentBottom,
    imageWrapper,
    testimonialQuote,
    expertDetails,
    expertNameTV,
    expertRoleTV,
    linkedInWrapper,
    linkedInIcon,
    separator,
    ctaWrapper,
    ctaLink,
  } = TAILWIND_VARIANTS({ colCount: colCount ?? '3' });

  return (
    <section
      id="search-result-cards"
      className={container()}
      {...getTestProps(`search-result-cards`)}
    >
      <div className={searchContainer()}>
        {/* Search Input */}
        <div className={searchSection()}>
          <div className={relativeWrapper()}>
            <div className={searchWrapper()}>
              <div className={searchIcon()}>
                <RemixIcon name="search-line" size="md" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                ref={inputRef}
                onFocus={() => setShowSuggestions(true)}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  // Check if the blur is caused by clicking on a suggestion
                  // If the relatedTarget (the element receiving focus) is within the dropdown, don't hide it
                  const relatedTarget = e.relatedTarget as HTMLElement;
                  if (relatedTarget && dropdownRef.current?.contains(relatedTarget)) {
                    return; // Don't hide dropdown if clicking inside it
                  }

                  // Check if the blur is caused by clicking the clear button
                  if (relatedTarget && clearButtonRef.current?.contains(relatedTarget)) {
                    return; // Don't hide dropdown if clicking the clear button
                  }

                  // If we're in the process of clearing, don't handle blur
                  if (isClearingRef.current) {
                    return;
                  }

                  // Delay hiding dropdown to allow for suggestion clicks
                  setTimeout(() => {
                    // Double-check that we're still not focused on dropdown elements or clearing
                    const activeElement = document.activeElement;
                    if (
                      !dropdownRef.current?.contains(activeElement) &&
                      !clearButtonRef.current?.contains(activeElement) &&
                      !isClearingRef.current
                    ) {
                      setShowSuggestions(false);
                      setSelectedSuggestionIndex(-1);
                    }
                  }, 150);
                }}
                placeholder={searchPlaceholder?.value || 'Search experts by name...'}
                className={searchInput()}
                aria-label={getDictionaryValue('ExpertListSearchAriaLabel') || 'Search experts'}
                aria-autocomplete="list"
                aria-controls="search-suggestions"
                aria-haspopup="listbox"
                {...getTestProps(`search-input`)}
              />
              {/* Clear Button - Show when there's input */}
              {searchQuery && !loadingSuggestions && (
                <button
                  ref={clearButtonRef}
                  type="button"
                  onClick={() => {
                    isClearingRef.current = true;

                    // Urgent: clear the input value immediately so the user
                    // sees their click acknowledged. The rest of the work
                    // (re-fetching results) goes through a transition so the
                    // cards don't flash empty before the new list arrives.
                    setSearchQuery('');
                    setShowSuggestions(false);

                    startWidgetTransition(() => {
                    setSubmittedSearch('');
                    setExactMatchName(null);
                    setFullSuggestionContext(null);
                    setDisplayCount(maxVisibleExperts);
                    lastGtmSearchTermRef.current = null;
                      if (typeof window !== 'undefined')
                        sessionStorage.removeItem(getStorageKey());
                    clearSuggestions();
                    performSearch('');

                    // Update URL to remove search parameter
                    if (isLandingPage) {
                      const urlParams = new URLSearchParams();
                      // Keep existing filters
                      if (selectedFilters.expertise)
                        urlParams.append('expertise', selectedFilters.expertise);
                      if (selectedFilters.location)
                        urlParams.append('location', selectedFilters.location);
                      if (selectedFilters.service)
                        urlParams.append('service', selectedFilters.service);

                      const currentPath = pathname;
                      const newUrl = urlParams.toString()
                        ? `${currentPath}?${urlParams.toString()}`
                        : currentPath;

                        // { scroll: false }: Next.js App Router scrolls to top by default on
                        // router.push. We want the user to stay where they are when filters /
                        // searches update the URL query string.
                        router.push(newUrl, { scroll: false });
                    }
                    });

                    // Use setTimeout to ensure focus happens after any blur events
                    setTimeout(() => {
                      inputRef.current?.focus();
                      setShowSuggestions(true);
                      // Reset the clearing flag after a short delay
                      setTimeout(() => {
                        isClearingRef.current = false;
                      }, 200);
                    }, 0);
                  }}
                  className={clearButton()}
                  aria-label={
                    getDictionaryValue('ExpertListClearSearchAriaLabel') || 'Clear search'
                  }
                  {...getTestProps(`clear-button`)}
                >
                  <RemixIcon name="close-line" size="md" />
                </button>
              )}
              {/* Loading Spinner */}
              {loadingSuggestions && (
                <div className={loadingSpinnerContainer()}>
                  <RemixIcon name="loader-4-line" size="md" className={loadingSpinnerIcon()} />
                </div>
              )}
              {/* Search Button - Only show when there's input */}
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => handleSearchSubmit()}
                  className={searchButton()}
                  aria-label={
                    getDictionaryValue('ExpertListSearchButtonAriaLabel') || 'Search experts'
                  }
                  {...getTestProps(`search-button`)}
                >
                  <RemixIcon name="arrow-right-line" size="md" />
                </button>
              )}
            </div>

            {/* Suggestions Dropdown */}
            <div id="search-suggestions">
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={dropdownRef}
                  className={suggestionsDropdown()}
                  role="listbox"
                  {...getTestProps(`search-suggestions`)}
                >
                  <div className={suggestionsLabel()}>
                    {getDictionaryValue('ExpertListSuggestionsLabel') || 'Suggestions'}
                  </div>
                  <div className={suggestionsList()}>
                    {suggestions.map((suggestion, index) => {
                      const formatSuggestionText = (text: string) => {
                        // First, split by pipe to separate company/location info
                        let mainPart = text;
                        let afterPipe = '';
                        if (text.includes('|')) {
                          const pipeParts = text.split('|').map((part) => part.trim());
                          mainPart = pipeParts[0];
                          afterPipe =
                            pipeParts.length > 1 ? ' | ' + pipeParts.slice(1).join(' | ') : '';
                        }
                        // Then, split the main part by dash to separate name from title
                        let namePart = mainPart;
                        let titlePart = '';
                        if (mainPart.includes('-')) {
                          const dashParts = mainPart.split('-').map((part) => part.trim());
                          namePart = dashParts[0];
                          titlePart =
                            dashParts.length > 1 ? ' - ' + dashParts.slice(1).join(' - ') : '';
                        }
                        // Capitalize each word in the name
                        const capitalizedName = namePart
                          .split(' ')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ');
                        const rest = titlePart + afterPipe;
                        return { name: capitalizedName, rest };
                      };
                      const { name, rest } = formatSuggestionText(suggestion.text);
                      return (
                        <button
                          key={index}
                          type="button"
                          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                            // Use onMouseDown to handle click before blur event fires
                            e.preventDefault(); // Prevent input blur
                            handleSuggestionClick(suggestion.text, index, e);
                          }}
                          className={suggestionItem({
                            selected: index === selectedSuggestionIndex,
                          })}
                          role="option"
                          aria-label={suggestion.text}
                          aria-selected={index === selectedSuggestionIndex}
                          tabIndex={-1}
                          {...getTestProps(`suggestion-${index}`)}
                        >
                          <span className={suggestionContent()}>
                            <RemixIcon name="search-line" size="md" className={suggestionIcon()} />
                            <span className={suggestionText()}>
                              <span className={suggestionName()}>{name}</span>
                              {rest && <span className={suggestionRest()}>{rest}</span>}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters - Show skeleton only on initial load, otherwise show actual filters or message */}
        {!initialSearchSettled ? (
          <SkeletonFilterPanel filterCount={2} />
        ) : facets.length > 0 ? (
          <div className={filtersSection()}>
            <PlainTextWrapper
              field={filterText?.value ? filterText : { value: 'Filter to browse our experts:' }}
              className={filterLabel()}
              {...getTestProps(`filter-label`)}
            />

            <div
              className={filterDropdowns({
                count: facets.length <= 3 ? (facets.length as 1 | 2 | 3) : 3,
              })}
              data-facet-count={facets.length}
            >
              {facets
                .slice()
                .sort((a, b) => {
                  // Put expertise filters first, then location filters
                  const aIsExpertise = a.name.toLowerCase().includes('expertise');
                  const bIsExpertise = b.name.toLowerCase().includes('expertise');

                  if (aIsExpertise && !bIsExpertise) return -1;
                  if (!aIsExpertise && bIsExpertise) return 1;
                  return 0;
                })
                .map((facet, idx) => {
                  // Transform facet options to react-select format
                  const options: SelectOptionType[] = [
                    {
                      value: '',
                      label: `${getDictionaryValue('ExpertListFilterAll') || 'All'} ${facet.label || facet.name}`,
                    },
                    ...(facet.value?.map((option) => ({
                      value: option.text, // Use text value like SearchResult.tsx does
                      label: `${option.text} (${option.count})`,
                    })) || []),
                  ];

                  // If selected value is not in options (e.g., when API doesn't return it due to facet.types filtering),
                  // add it manually so the dropdown shows the correct selection
                  const selectedFilterValue =
                    selectedFilters[facet.name as keyof typeof selectedFilters];
                  if (
                    selectedFilterValue &&
                    !options.find((opt) => opt.value === selectedFilterValue)
                  ) {
                    // The count should be the total number of experts matching ALL active filters
                    options.push({
                      value: selectedFilterValue,
                      label: `${selectedFilterValue} (${allExperts.length})`,
                    });
                  }

                  const selectedValue =
                    options.find(
                      (opt) =>
                        opt.value === selectedFilters[facet.name as keyof typeof selectedFilters]
                    ) || options[0];

                  return (
                    <Select<SelectOptionType>
                      key={idx}
                      inputId={`filter-${facet.name}`}
                      value={selectedValue}
                      onChange={(option) => handleFilterChange(facet.name, option)}
                      options={options}
                      styles={
                        getFormSelectStyles(
                          false,
                          false
                        ) as unknown as StylesConfig<SelectOptionType>
                      }
                      components={{ DropdownIndicator }}
                      classNamePrefix="react-select"
                      aria-label={`Filter by ${facet.label || facet.name}`}
                      {...getTestProps(`filter-${facet.name}`)}
                    />
                  );
                })}
            </div>
          </div>
        ) : (
          <div className={filtersNotAvailable()}>
            <p>
              {getDictionaryValue('ExpertListFiltersNotAvailable') || 'Filters are not available.'}
            </p>
          </div>
        )}
      </div>

      {/* Results */}
      <div className={resultsSection()}>
        {showExpertResultsSkeleton ? (
          <div className={expertGrid()} {...getTestProps(`loading`)}>
            {[...Array(maxVisibleExperts)].map((_, index) => (
              <SkeletonCard key={index} variant="expert" orientation="horizontal" />
            ))}
          </div>
        ) : error ? (
          <div className={noResults()} {...getTestProps(`error`)}>
            <p className={noResultsText()}>
              {getDictionaryValue('ExpertListError') || 'Error'}: {error}
            </p>
          </div>
        ) : visibleExperts.length > 0 ? (
          <>
            <div className={expertGrid()}>
              {visibleExperts.map((expert, index) => (
                <article
                  key={expert.id || index}
                  className={expertCard()}
                  {...getTestProps(`expert-card-${index}`)}
                >
                  {/* Image Section */}
                  <div className={imageSection()}>
                    {expert?.picture ? (
                      <ImageWrapper
                        field={{
                          value: {
                            src: expert.picture,
                            alt:
                              expert.name || getDictionaryValue('ExpertListExpertAlt') || 'Expert',
                          },
                        }}
                        className={imageWrapper()}
                        {...getTestProps(`expert-image-${index}`)}
                      />
                    ) : (
                        <ImageWrapper
                          field={{
                            value: {
                              src: '/assets/profile_placeholder.png',
                              alt: 'No image',
                            },
                          }}
                          className={imageWrapper()}
                          {...getTestProps(`related-profile-image-placeholder`)}
                        />
                    )}
                  </div>

                  {/* Content Section - Dark Blue Background */}
                  <div className={contentSection()}>
                    <div className={contentTop()}>
                      {/* Testimonial Quote */}
                      {expert?.quote && (
                        <RichTextWrapper
                          field={{ value: expert.quote }}
                          className={`text-component-expert-testimonial ${testimonialQuote()}`}
                          {...getTestProps(`expert-testimonial-${index}`)}
                        />
                      )}

                      {/* Expert Details */}
                      <div className={expertDetails()}>
                        <div>
                          <PlainTextWrapper
                            className={expertNameTV()}
                            field={{
                              value: `${expert.first_name || ''} ${expert.last_name || ''}`.trim(),
                            }}
                            tag="div"
                            {...getTestProps(`expert-name-${index}`)}
                          />
                          {expert.job_title && (
                            <PlainTextWrapper
                              className={expertRoleTV()}
                              field={{ value: expert.job_title }}
                              tag="div"
                              {...getTestProps(`expert-role-${index}`)}
                            />
                          )}
                        </div>

                        {/* LinkedIn Link */}
                        {expert.linked_in_url && (
                          <LinkWrapper
                            ctaComponentClass="component-card-item-button-1-color"
                            field={{ value: { href: expert.linked_in_url, target: '_blank' } }}
                            className={linkedInWrapper()}
                            editable={false}
                            aria-label={`View ${expert.first_name} ${expert.last_name} LinkedIn profile`}
                            suppressNewTabIcon={true}
                            {...getTestProps(`expert-linkedin-${index}`)}
                          >
                            <RemixIcon
                              name="linkedin-box-fill"
                              size="xl"
                              className={linkedInIcon()}
                            />
                          </LinkWrapper>
                        )}
                      </div>
                    </div>

                    {/* Separator and CTA */}
                    <div className={contentBottom()}>
                      {expert.url && <div className={separator()}></div>}

                      {expert.url && (
                        <div className={ctaWrapper()}>
                          <LinkWrapper
                            ctaComponentClass="component-card-item-button-1-color"
                            className={ctaLink()}
                            field={{
                              value: {
                                href: expert.url,
                                text: cardsCTAText?.value || 'Find out more',
                              },
                            }}
                            ctaIcon="arrow-right"
                            ctaIconSize="s"
                            ctaVariant="tertiaryInverse"
                            onClick={() => {
                              void trackSearchItemClick({
                                widgetId: rfkId || 'rfkid_expert_profile',
                                entity: {
                                  id: expert.id,
                                  entity: 'expertprofile',
                                  uri: expert.url,
                                },
                                itemPosition: index,
                              });
                            }}
                            aria-label={
                              cardsCTAText?.value
                                ? `${cardsCTAText?.value} for ${expert.first_name} ${expert.last_name}`
                                : `Find out more about ${expert.first_name} ${expert.last_name}`
                            }
                            {...getTestProps(`expert-cta-${index}`)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Show More Button - Only show on landing page if there are more experts */}
            {/* View All Experts CTA - Show on non-landing pages (using cta2) */}
            {isLandingPage
              ? hasMoreExperts && (
                  <div className={ctaSection()}>
                    <ButtonWrapper
                      type="button"
                      text={getDictionaryValue('ExpertListShowMore') || 'Show more experts'}
                      ctaComponentClass="component-card-item-button-2-color"
                      className={`${ctaButton()} [&_svg]:rotate-90`}
                      ctaVariant="secondary"
                      ctaIcon="arrow-right"
                      onClick={handleShowMore}
                      {...getTestProps(`show-more-experts`)}
                    />
                  </div>
                )
              : cta2WithParams && (
                  <div className={ctaSection()}>
                    <LinkWrapper
                      field={cta2WithParams}
                      ctaComponentClass="component-card-item-button-2-color"
                      className={ctaButton()}
                      ctaVariant="secondary"
                      ctaIcon="arrow-right"
                      suppressNewTabIcon={true}
                      {...getTestProps(`view-all-experts-cta`)}
                    />
                  </div>
                )}
          </>
        ) : (
          <div className={noResults()} {...getTestProps(`no-results`)}>
            <RichTextWrapper
              className={noResultsText()}
              field={
                noSearchResult?.value
                  ? noSearchResult
                  : {
                      value: 'Sorry there are <strong>no results based on that criteria.</strong>',
                    }
              }
              tag="div"
              {...getTestProps(`no-results-text`)}
            />

            {/* No Results CTAs */}
            {(cta1Field || (!isLandingPage && cta2Field)) && (
              <div className={noResultsCtaSection()}>
                {cta1Field && (
                  <LinkWrapper
                    field={cta1Field}
                    ctaComponentClass="component-card-item-button-1-color"
                    className={noResultsCtaButton()}
                    ctaVariant="primary"
                    ctaIcon="arrow-right"
                    suppressNewTabIcon={true}
                    {...getTestProps(`no-results-cta1`)}
                  />
                )}
                {!isLandingPage && cta2WithParams && (
                  <LinkWrapper
                    field={cta2WithParams}
                    ctaComponentClass="component-card-item-button-2-color"
                    className={noResultsCtaButton()}
                    ctaVariant="secondary"
                    ctaIcon="arrow-right"
                    suppressNewTabIcon={true}
                    {...getTestProps(`no-results-cta2`)}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default React.memo(ExpertListWithSearchComponent);

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['w-full', 'flex', 'flex-col', 'gap-spacing-spacing-40', '[overflow-anchor:none]'],
    searchContainer: [
      'w-full',
      'max-w-4xl',
      'mx-auto',
      'flex',
      'flex-col',
      'gap-spacing-spacing-24',
    ],
    searchSection: ['w-full'],
    searchWrapper: [
      'flex',
      'h-12',
      'px-spacing-spacing-8',
      'items-center',
      'gap-spacing-spacing-8',
      'self-stretch',
      'border',
      'border-color-input-border',
      'rounded-general-border-radius-button',
      'bg-white',
      'hover:border-color-input-border-hover',
      'focus-within:border-color-input-border-focused',
      'focus-within:ring-1',
      'focus-within:ring-color-input-border-focused',
    ],
    searchIcon: ['text-color-grayscale-black', 'pointer-events-none', 'w-6', 'h-6'],
    searchInput: [
      'flex-1',
      'bg-transparent',
      'border-none',
      'outline-none',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'text-component-section-body',
      'placeholder:text-color-text-text-secondary',
      'placeholder:font-typography-font-family-body',
      'placeholder:text-typography-font-size-body-medium',
      'placeholder:italic',
      'placeholder:font-normal',
      'placeholder:leading-typography-line-height-body-medium',
    ],
    clearButton: [
      'absolute',
      'right-12',
      'top-1/2',
      '-translate-y-1/2',
      'w-8',
      'h-8',
      'flex',
      'items-center',
      'justify-center',
      'rounded-general-border-radius-button',
      'bg-white',
      'border',
      'border-component-button-tertiary-icon',
      'text-component-button-tertiary-icon',
      'hover:bg-color-bg-secondary',
      'transition-colors',
    ],
    loadingSpinnerContainer: [
      'absolute',
      'right-14',
      'top-1/2',
      '-translate-y-1/2',
      'w-8',
      'h-8',
      'flex',
      'items-center',
      'justify-center',
    ],
    searchButton: [
      'absolute',
      'right-3',
      'top-1/2',
      '-translate-y-1/2',
      'w-8',
      'h-8',
      'flex',
      'items-center',
      'justify-center',
      'rounded-general-border-radius-button',
      'bg-component-button-tertiary-icon',
      'text-component-button-tertiary-text',
      'hover:bg-color-core-color-4-SkyBlue',
      'transition-colors',
    ],
    relativeWrapper: ['relative'],
    suggestionsDropdown: [
      'absolute',
      'z-10',
      'w-full',
      'mt-2',
      'flex',
      'flex-col',
      'items-start',
      'p-spacing-spacing-24',
      'gap-spacing-spacing-24',
      'rounded-general-border-radius-card',
      'border',
      'border-color-border-border-hover',
      'bg-color-bg-white',
    ],
    suggestionsLabel: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'text-component-section-body',
    ],
    suggestionsList: ['flex', 'flex-col', 'gap-spacing-spacing-8', 'w-full'],
    suggestionIcon: ['text-component-button-tertiary-icon'],
    loadingSpinnerIcon: ['animate-spin', 'text-color-core-color-4-skyblue-darkTint'],
    filtersNotAvailable: ['text-center', 'py-4', 'text-color-text-text-secondary', 'text-sm'],
    suggestionItem: [
      'w-full',
      'text-left',
      'flex',
      'items-center',
      'justify-between',
      'py-spacing-spacing-2',
      'transition-colors',
      'cursor-pointer',
      'hover:bg-color-core-color-4-skyblue-lightTint',
    ],
    suggestionContent: ['flex', 'items-center', 'gap-spacing-spacing-8'],
    suggestionText: [
      'text-component-button-tertiary-text',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-xxsmall',
    ],
    suggestionName: ['font-semibold'],
    suggestionRest: ['font-normal'],
    filtersSection: [
      'flex',
      'flex-col',
      'p-spacing-spacing-16',
      'gap-spacing-spacing-16',
      'self-stretch',
      'rounded-border-radius-radius-4',
      'bg-color-bg-white',
      'shadow-[0_0_8px_0_var(--color-black-alpha-10)]',
    ],
    filterLabel: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'text-component-section-body',
      'text-left',
    ],
    filterDropdowns: [
      'grid',
      'grid-cols-1',
      'gap-x-space-between-less',
      'gap-y-spacing-spacing-16',
    ],
    resultsSection: ['w-full'],
    expertGrid: [
      'grid',
      'grid-cols-1',
      'md:grid-cols-1',
      'gap-spacing-spacing-24',
      'lg:gap-spacing-spacing-32',
    ],
    noResults: [
      'flex',
      'flex-col',
      'items-center',
      'justify-center',
      'gap-component-section-container-gutter',
      'text-center',
    ],
    noResultsText: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
      'text-color-grayscale-black',
      'max-w-columns-two-third-max-width',
    ],
    ctaSection: ['flex', 'justify-center', 'pt-spacing-spacing-32', 'w-full'],
    ctaButton: [],
    noResultsCtaSection: [
      'flex',
      'flex-col',
      'md:flex-row',
      'gap-spacing-spacing-16',
      'justify-center',
      'pt-spacing-spacing-24',
      'w-full',
    ],
    noResultsCtaButton: [],
    loadingState: [
      'flex',
      'flex-col',
      'items-center',
      'justify-center',
      'gap-spacing-spacing-16',
      'py-spacing-spacing-64',
      'text-color-text-text-secondary',
    ],
    loadingSpinner: ['animate-spin', 'text-color-core-color-4-SkyBlue'],
    // Expert Card Styles
    expertCard: [
      'flex',
      'flex-col',
      'md:flex-row',
      'lg:flex-col',
      'w-full',
      'h-full',
      'min-h-[500px]', // Set min height to reduce layout shift
      'md:min-h-[350px]',
      'lg:min-h-[550px]',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
      'animate-[fadeIn_0.3s_ease-in]',
    ],
    imageSection: [
      'relative',
      'w-full',
      'md:w-1/2',
      'lg:w-full',
      'overflow-hidden',
      'aspect-[4/3]',
      'md:aspect-square',
      'lg:aspect-[4/3]',
      'rounded-t-general-border-radius-card',
      'md:rounded-l-general-border-radius-card',
      'md:rounded-tr-none',
      'lg:rounded-l-none',
      'lg:rounded-t-general-border-radius-card',
    ],
    contentSection: [
      'flex',
      'flex-col',
      'flex-1',
      'w-full',
      'md:w-1/2',
      'lg:w-full',
      'gap-spacing-spacing-24',
      'py-component-card-item-landscape-content-1-col-padding-y',
      'px-component-card-item-landscape-content-1-col-padding-x',
      'bg-component-featureCard-surface',
      'rounded-b-general-border-radius-card',
      'md:rounded-r-general-border-radius-card',
      'md:rounded-bl-none',
      'lg:rounded-r-none',
      'lg:rounded-b-general-border-radius-card',
    ],
    contentTop: ['flex', 'flex-col', 'gap-spacing-spacing-24'],
    contentBottom: ['flex', 'flex-col', 'gap-spacing-spacing-24', 'mt-auto'],
    imageWrapper: ['w-full', 'h-full', 'object-cover'],
    testimonialQuote: [
      'text-white',
      'font-typography-font-family-header',
      'text-typography-font-size-header-small',
      'font-normal',
      'leading-typography-line-height-small',
      'text-left',
      'relative',
    ],
    expertDetails: ['flex', 'items-start', 'justify-between', 'gap-spacing-spacing-16'],
    expertNameTV: [
      'text-white',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'text-left',
      'leading-normal',
    ],
    expertRoleTV: [
      'text-white',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-normal',
      'leading-normal',
      'text-left',
      'mt-spacing-spacing-4',
    ],
    linkedInWrapper: [
      'flex',
      'w-10',
      'h-10',
      '!p-0',
      'text-white',
      'items-center',
      'justify-center',
      'hover:opacity-80',
      'transition-opacity',
      'duration-200',
      '[&:hover_svg]:!text-white',
    ],
    linkedInIcon: ['text-white'],
    separator: ['w-full', 'h-[2px]', 'bg-white'],
    ctaWrapper: ['flex', 'p-spacing-spacing-2', 'items-center', 'justify-between', 'self-stretch'],
    ctaLink: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'leading-typography-line-height-xxsmall',
      'flex',
      'items-center',
      'justify-between',
      'w-full',
      'transition-opacity',
      'duration-200',
      '[&:hover_svg]:!text-white',
    ],
  },
  variants: {
    colCount: {
      '1': { expertGrid: ['lg:grid-cols-1'] },
      '2': { expertGrid: ['lg:grid-cols-2'] },
      '3': { expertGrid: ['lg:grid-cols-3'] },
      '4': { expertGrid: ['lg:grid-cols-4'] },
    },
    count: {
      1: {
        filterDropdowns: ['lg:grid-cols-1'],
      },
      2: {
        filterDropdowns: ['lg:grid-cols-2'],
      },
      3: {
        filterDropdowns: ['lg:grid-cols-3'],
      },
      4: {
        filterDropdowns: ['lg:grid-cols-4'],
      },
    },
    selected: {
      true: {
        suggestionItem: ['bg-color-core-color-4-skyblue-lightTint'],
      },
    },
  },
  defaultVariants: {
    colCount: '3',
  },
});
